import { observable, action } from "mobx";
import {modalService} from "@app/Components/Modal";

const UNSAVED_CHANGES_CONFIRMATION = "There are unsaved changes. Do you want to continue?";

async function confirmDiscard(isDirty: boolean): Promise<boolean> {
    if (!isDirty) {
        return true;
    }

    return await modalService.showConfirmation(UNSAVED_CHANGES_CONFIRMATION);
}

export class UnsavedChangesSource {
    constructor(private readonly _store: UnsavedChangesStore, private readonly _key: string) {
    }

    get isDirty(): boolean {
        return this._store.isSourceDirty(this._key);
    }

    setDirty(isDirty: boolean) {
        this._store.setSourceDirty(this._key, isDirty);
    }

    markDirty() {
        this.setDirty(true);
    }

    reset() {
        this.setDirty(false);
    }

    showConfirmation(): Promise<boolean> {
        return confirmDiscard(this.isDirty);
    }

    dispose() {
        this.reset();
    }
}

export class UnsavedChangesStore {
    private readonly _dirtySourceKeys = observable.map<string, boolean>();
    private _registeredSourceCount = 0;

    constructor() {
        window.addEventListener('beforeunload', this._onBeforeUnload);
    }

    get hasUnsavedChanges(): boolean {
        return this._dirtySourceKeys.size > 0;
    }

    registerSource(name: string): UnsavedChangesSource {
        this._registeredSourceCount++;

        return new UnsavedChangesSource(this, `${name}#${this._registeredSourceCount}`);
    }

    isSourceDirty(sourceKey: string): boolean {
        return this._dirtySourceKeys.has(sourceKey);
    }

    @action.bound
    setSourceDirty(sourceKey: string, isDirty: boolean) {
        if (isDirty) {
            this._dirtySourceKeys.set(sourceKey, true);
        } else {
            this._dirtySourceKeys.delete(sourceKey);
        }
    }

    @action.bound
    resetAll() {
        this._dirtySourceKeys.clear();
    }

    showConfirmation(): Promise<boolean> {
        return confirmDiscard(this.hasUnsavedChanges);
    }

    private _onBeforeUnload = (e: BeforeUnloadEvent) => {
        if (!this.hasUnsavedChanges) {
            return;
        }

        e.preventDefault();
        e.returnValue = '';
    };
}

export const unsavedChangesStore = new UnsavedChangesStore();
